import type { ReactNode } from 'react'

import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Spinner } from '@/components/ui/Spinner'

/**
 * The loading / forbidden / failed / empty states every list screen needs, in one place.
 *
 * 403 gets its own branch on purpose. A user who reaches a screen they lack permission for
 * should be told that plainly -- rendering "something went wrong" for a rule working
 * exactly as intended sends people to support for no reason.
 */
export function QueryState({
  isLoading,
  error,
  isEmpty,
  emptyMessage = 'Nothing to show yet.',
  skeleton,
  children,
}: {
  isLoading: boolean
  error: unknown
  isEmpty?: boolean
  emptyMessage?: string
  /**
   * Shown instead of the spinner while loading. Worth passing wherever the shape of the
   * result is known before it arrives — the page keeps its dimensions and nothing jumps
   * when the data lands. A spinner is the right answer only when it is not.
   */
  skeleton?: ReactNode
  children: ReactNode
}) {
  if (isLoading) {
    if (skeleton) return <>{skeleton}</>
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-slate-500" aria-busy="true">
        <Spinner className="h-4 w-4" />
        Loading…
      </div>
    )
  }

  if (error) {
    if (error instanceof ApiError && error.status === 403) {
      return (
        <Alert tone="info" title="Not available to your role">
          {error.message} Ask an administrator if you need access.
        </Alert>
      )
    }
    const message =
      error instanceof ApiError ? error.message : 'Could not reach the server. Try again.'
    return (
      <Alert tone="error" title="Could not load this">
        {message}
      </Alert>
    )
  }

  if (isEmpty) {
    return <p className="p-6 text-sm text-slate-500 dark:text-slate-400">{emptyMessage}</p>
  }

  return <>{children}</>
}
